import SignalBroker, { EmitOptions } from "./engine/SignalBroker";
import GraphRunner from "./engine/GraphRunner";
import GraphRegistry from "./registry/GraphRegistry";
import Task, { TaskFunction, ThrottleTagGetter } from "./graph/definition/Task";
import DebounceTask, { DebounceOptions } from "./graph/definition/DebounceTask";
import EphemeralTask, {
  EphemeralTaskOptions,
} from "./graph/definition/EphemeralTask";
import GraphRoutine from "./graph/definition/GraphRoutine";
import GraphAsyncRun from "./engine/strategy/GraphAsyncRun";
import GraphStandardRun from "./engine/strategy/GraphStandardRun";
import { Schema } from "./types/schema";
import { AnyObject } from "./types/global";
import InquiryBroker, { InquiryOptions, Intent } from "./engine/InquiryBroker";
import Actor, {
  ActorDefinition,
  ActorFactoryOptions,
  ActorSpec,
  getActorTaskRuntimeMetadata,
} from "./actors/Actor";
import {
  GlobalDefinition,
  HelperDefinition,
  type HelperFunction,
  type RuntimeTools,
  type ToolDependencyOwner,
} from "./tools/definitions";
import {
  runtimeDefinitionRegistry,
} from "./runtime/RuntimeDefinitionRegistry";
import {
  compileRuntimeHelperFunction,
  compileRuntimeTaskFunction,
} from "./runtime/sandbox";
import { sanitizeForJson } from "./runtime/sanitize";
import type {
  RuntimeActorTaskDefinition,
  RuntimeGlobalDefinition,
  RuntimeHelperDefinition,
  RuntimeRoutineDefinition,
  RuntimeSnapshot,
  RuntimeTaskDefinition,
} from "./runtime/types";

export interface TaskOptions {
  concurrency?: number;
  timeout?: number;
  register?: boolean;
  isUnique?: boolean;
  isMeta?: boolean;
  isSubMeta?: boolean;
  isHidden?: boolean;
  getTagCallback?: ThrottleTagGetter;
  inputSchema?: Schema;
  validateInputContext?: boolean;
  outputSchema?: Schema;
  validateOutputContext?: boolean;
  retryCount?: number;
  retryDelay?: number;
  retryDelayMax?: number;
  retryDelayFactor?: number;
}

export type CadenzaMode = "dev" | "debug" | "verbose" | "production";
export type RuntimeValidationMode = "off" | "warn" | "enforce";
export interface RuntimeValidationPolicy {
  metaInput?: RuntimeValidationMode;
  metaOutput?: RuntimeValidationMode;
  businessInput?: RuntimeValidationMode;
  businessOutput?: RuntimeValidationMode;
  warnOnMissingMetaInputSchema?: boolean;
  warnOnMissingMetaOutputSchema?: boolean;
  warnOnMissingBusinessInputSchema?: boolean;
  warnOnMissingBusinessOutputSchema?: boolean;
}
export interface RuntimeValidationScope {
  id: string;
  active?: boolean;
  startTaskNames?: string[];
  startRoutineNames?: string[];
  policy?: RuntimeValidationPolicy;
}
export interface ResolvedRuntimeValidationPolicy {
  layer: "meta" | "business";
  inputMode: RuntimeValidationMode;
  outputMode: RuntimeValidationMode;
  warnOnMissingInputSchema: boolean;
  warnOnMissingOutputSchema: boolean;
  activeScopeIds: string[];
}
export type RuntimeInquiryDelegate = (
  inquiry: string,
  context: AnyObject,
  options?: InquiryOptions,
) => Promise<any>;

/**
 * Represents the core class of the Cadenza framework managing tasks, meta-tasks, signal emissions, and execution strategies.
 * All core components such as SignalBroker, GraphRunner, and GraphRegistry are initialized through this class, and it provides
 * utility methods to create, register, and manage various task types.
 */
export default class Cadenza {
  public static signalBroker: SignalBroker;
  public static inquiryBroker: InquiryBroker;
  public static runner: GraphRunner;
  public static metaRunner: GraphRunner;
  public static registry: GraphRegistry;
  private static taskCache: Map<string, Task> = new Map();
  private static routineCache: Map<string, GraphRoutine> = new Map();
  private static actorCache: Map<string, Actor<any, any>> = new Map();
  private static helperCache: Map<string, HelperDefinition> = new Map();
  private static globalCache: Map<string, GlobalDefinition> = new Map();
  private static runtimeInquiryDelegate: RuntimeInquiryDelegate | undefined;
  private static runtimeValidationPolicy: RuntimeValidationPolicy = {};
  private static runtimeValidationScopes: Map<string, RuntimeValidationScope> =
    new Map();
  private static emittedMissingSchemaWarnings: Set<string> = new Set();
  private static helperExecutionDepth = 0;
  static isBootstrapped = false;
  static mode: CadenzaMode = "production";

  /**
   * Initializes the system by setting up the required components such as the
   * signal broker, runners, and graph registry. Ensures the initialization
   * happens only once. Configures debug settings if applicable.
   *
   * @return {void} No value is returned.
   */
  public static bootstrap(): void {
    if (this.isBootstrapped) return;
    this.isBootstrapped = true;

    // 1. Brokers (empty, for observations)
    this.signalBroker = SignalBroker.instance;
    this.inquiryBroker = InquiryBroker.instance;

    // 2. Runners (now init broker with them)
    this.runner = new GraphRunner();
    this.metaRunner = new GraphRunner(true);
    this.signalBroker.bootstrap(this.runner, this.metaRunner);
    this.inquiryBroker.bootstrap(this.runner, this.metaRunner);

    if (this.mode === "debug" || this.mode === "dev") {
      this.signalBroker.setDebug(true);
      this.inquiryBroker.setDebug(true);
      this.runner.setDebug(true);
      this.metaRunner.setDebug(true);
    }

    // 3. GraphRegistry (seed observes on broker)
    this.registry = GraphRegistry.instance;

    this.signalBroker.init();
    this.inquiryBroker.init();
  }

  /**
   * Retrieves the available strategies for running graphs.
   *
   * @return {Object} An object containing the available run strategies, where:
   *                  - PARALLEL: Executes graph runs asynchronously.
   *                  - SEQUENTIAL: Executes graph runs in a sequential order.
   */
  public static get runStrategy() {
    return {
      PARALLEL: new GraphAsyncRun(),
      SEQUENTIAL: new GraphStandardRun(),
    };
  }

  /**
   * Sets the mode for the application and configures the broker and runner settings accordingly.
   *
   * @param {CadenzaMode} mode - The mode to set. It can be one of the following:
   *                             "debug", "dev", "verbose", or "production".
   *                             Each mode adjusts debug and verbosity settings.
   * @return {void} This method does not return a value.
   */
  public static setMode(mode: CadenzaMode) {
    this.mode = mode;

    this.bootstrap();

    if (mode === "debug" || mode === "dev") {
      this.signalBroker.setDebug(true);
      this.runner.setDebug(true);
    }

    if (mode === "verbose") {
      this.signalBroker.setDebug(true);
      this.signalBroker.setVerbose(true);
      this.runner.setDebug(true);
      this.runner.setVerbose(true);
    }

    if (mode === "production") {
      this.signalBroker.setDebug(false);
      this.signalBroker.setVerbose(false);
      this.runner.setDebug(false);
      this.runner.setVerbose(false);
    }
  }

  /**
   * Validates the given name to ensure it is a non-empty string.
   * Throws an error if the validation fails.
   *
   * @param {string} name - The name to validate.
   * @return {void} This method does not return anything.
   * @throws {Error} If the name is not a non-empty string.
   */
  public static validateName(name: string): void {
    if (!name || typeof name !== "string") {
      throw new Error("Task or Routine name must be a non-empty string.");
    }
    // Further uniqueness check delegated to GraphRegistry.register*
  }

  public static assertGraphMutationAllowed(operationName: string): void {
    if (this.helperExecutionDepth > 0) {
      throw new Error(
        `${operationName} is not allowed during helper execution`,
      );
    }
  }

  public static executeHelper(
    helper: HelperDefinition,
    context: AnyObject,
    emit: (signal: string, context: AnyObject) => void,
    inquire: (
      inquiry: string,
      context: AnyObject,
      options: InquiryOptions,
    ) => Promise<AnyObject>,
    progressCallback: (progress: number) => void,
  ) {
    const helperContext =
      context && typeof context === "object" ? context : {};
    const tools = this.resolveToolsForOwner(
      helper,
      helperContext,
      emit,
      inquire,
      progressCallback,
    );

    this.helperExecutionDepth += 1;
    try {
      return helper.helperFunction(
        helperContext,
        emit,
        inquire,
        tools,
        progressCallback,
      );
    } finally {
      this.helperExecutionDepth = Math.max(0, this.helperExecutionDepth - 1);
    }
  }

  public static resolveToolsForOwner(
    owner: ToolDependencyOwner,
    context: AnyObject,
    emit: (signal: string, context: AnyObject) => void,
    inquire: (
      inquiry: string,
      context: AnyObject,
      options: InquiryOptions,
    ) => Promise<AnyObject>,
    progressCallback: (progress: number) => void,
  ): RuntimeTools {
    const helpers: RuntimeTools["helpers"] = {};
    const globals: RuntimeTools["globals"] = {};

    for (const [alias, helperName] of owner.helperAliases.entries()) {
      const helper = this.getHelper(helperName);
      if (!helper) {
        continue;
      }
      if (helper.isMeta !== owner.isMeta) {
        throw new Error(
          `Tool alias "${alias}" resolves across layer boundaries`,
        );
      }

      helpers[alias] = (helperContext: AnyObject = context) =>
        this.executeHelper(
          helper,
          helperContext,
          emit,
          inquire,
          progressCallback,
        );
    }

    for (const [alias, globalName] of owner.globalAliases.entries()) {
      const globalDefinition = this.getGlobal(globalName);
      if (!globalDefinition) {
        continue;
      }
      if (globalDefinition.isMeta !== owner.isMeta) {
        throw new Error(
          `Global alias "${alias}" resolves across layer boundaries`,
        );
      }
      globals[alias] = globalDefinition.value;
    }

    return {
      helpers,
      globals,
    };
  }

  private static resolveTaskOptionsForActorTask(
    func: TaskFunction,
    options: TaskOptions = {},
  ): TaskOptions {
    const metadata = getActorTaskRuntimeMetadata(func);
    if (!metadata?.forceMeta) {
      return options;
    }

    if (options.isMeta) {
      return options;
    }

    return {
      ...options,
      isMeta: true,
    };
  }

  private static registerActor(actor: Actor<any, any>): void {
    this.actorCache.set(actor.spec.name, actor);
  }

  public static getHelper(name: string): HelperDefinition | undefined {
    return this.helperCache.get(name);
  }

  public static getAllHelpers(): HelperDefinition[] {
    return Array.from(this.helperCache.values()).filter(
      (helper) => !helper.destroyed,
    );
  }

  public static getGlobal(name: string): GlobalDefinition | undefined {
    return this.globalCache.get(name);
  }

  public static getAllGlobals(): GlobalDefinition[] {
    return Array.from(this.globalCache.values()).filter(
      (globalDefinition) => !globalDefinition.destroyed,
    );
  }

  /**
   * Executes the specified task or GraphRoutine with the given context using an internal runner.
   *
   * @param {Task | GraphRoutine} task - The task or GraphRoutine to be executed.
   * @param {AnyObject} context - The context in which the task or GraphRoutine should be executed.
   * @return {void}
   *
   * @example
   * ```ts
   * const task = Cadenza.createTask('My task', (ctx) => {
   *   console.log('My task executed with context:', ctx);
   * });
   *
   * Cadenza.run(task, { foo: 'bar' });
   *
   * const routine = Cadenza.createRoutine('My routine', [task], 'My routine description');
   *
   * Cadenza.run(routine, { foo: 'bar' });
   * ```
   */
  public static run(task: Task | GraphRoutine, context: AnyObject) {
    this.runner?.run(task, context);
  }

  /**
   * Emits an event with the specified name and data payload to the broker.
   *
   * @param {string} event - The name of the event to emit.
   * @param {AnyObject} [data={}] - The data payload associated with the event.
   * @param options
   * @return {void} - No return value.
   *
   * @example
   * This is meant to be used as a global event emitter.
   * If you want to emit an event from within a task, you can use the `emit` method provided to the task function. See {@link TaskFunction}.
   * ```ts
   * Cadenza.emit('main.my_event', { foo: 'bar' });
   * ```
   */
  public static emit(
    event: string,
    data: AnyObject = {},
    options: EmitOptions = {},
  ) {
    this.signalBroker?.emit(event, data, options);
  }

  public static schedule(
    signalName: string,
    context: AnyObject,
    delayMs: number,
    exactDateTime?: Date,
  ) {
    this.signalBroker?.schedule(signalName, context, {
      delayMs,
      exactDateTime,
    });
  }

  public static interval(
    taskName: string,
    context: AnyObject,
    intervalMs: number,
    leading = false,
    startDateTime?: Date,
  ) {
    this.signalBroker?.interval(
      taskName,
      context,
      intervalMs,
      leading,
      startDateTime,
    );
  }

  public static debounce(signalName: string, context: any, delayMs: number) {
    this.signalBroker?.debounce(signalName, context, { delayMs });
  }

  public static get(taskName: string): Task | undefined {
    return this.registry?.tasks.get(taskName) ?? this.taskCache.get(taskName);
  }

  public static forgetTask(taskName: string): void {
    this.taskCache.delete(taskName);
  }

  public static getActor<
    D extends Record<string, any> = AnyObject,
    R = AnyObject,
  >(actorName: string): Actor<D, R> | undefined {
    return this.actorCache.get(actorName) as Actor<D, R> | undefined;
  }

  public static getAllActors<
    D extends Record<string, any> = AnyObject,
    R = AnyObject,
  >(): Actor<D, R>[] {
    return Array.from(this.actorCache.values()) as Actor<D, R>[];
  }

  public static getRoutine(routineName: string): GraphRoutine | undefined {
    return (
      this.registry?.routines.get(routineName) ?? this.routineCache.get(routineName)
    );
  }

  public static defineIntent(intent: Intent): Intent {
    this.assertGraphMutationAllowed("Cadenza.defineIntent");
    this.inquiryBroker?.addIntent(intent);
    return intent;
  }

  public static async inquire(
    inquiry: string,
    context: AnyObject,
    options?: InquiryOptions,
  ): Promise<any> {
    return this.inquiryBroker?.inquire(inquiry, context, options);
  }

  public static setRuntimeInquiryDelegate(
    delegate?: RuntimeInquiryDelegate,
  ): void {
    this.runtimeInquiryDelegate = delegate;
  }

  public static resolveRuntimeInquiryDelegate(): RuntimeInquiryDelegate {
    return (
      this.runtimeInquiryDelegate ??
      ((inquiry: string, context: AnyObject, options?: InquiryOptions) =>
        this.inquire(inquiry, context, options))
    );
  }

  public static getRuntimeValidationPolicy(): RuntimeValidationPolicy {
    return { ...this.runtimeValidationPolicy };
  }

  public static setRuntimeValidationPolicy(
    policy: RuntimeValidationPolicy = {},
  ): RuntimeValidationPolicy {
    this.runtimeValidationPolicy = {
      ...this.runtimeValidationPolicy,
      ...policy,
    };
    this.emittedMissingSchemaWarnings.clear();
    return this.getRuntimeValidationPolicy();
  }

  public static replaceRuntimeValidationPolicy(
    policy: RuntimeValidationPolicy = {},
  ): RuntimeValidationPolicy {
    this.runtimeValidationPolicy = { ...policy };
    this.emittedMissingSchemaWarnings.clear();
    return this.getRuntimeValidationPolicy();
  }

  public static clearRuntimeValidationPolicy(): void {
    this.runtimeValidationPolicy = {};
    this.emittedMissingSchemaWarnings.clear();
  }

  public static getRuntimeValidationScopes(): RuntimeValidationScope[] {
    return Array.from(this.runtimeValidationScopes.values()).map((scope) => ({
      ...scope,
      startTaskNames: scope.startTaskNames
        ? [...scope.startTaskNames]
        : undefined,
      startRoutineNames: scope.startRoutineNames
        ? [...scope.startRoutineNames]
        : undefined,
      policy: scope.policy ? { ...scope.policy } : undefined,
    }));
  }

  public static upsertRuntimeValidationScope(
    scope: RuntimeValidationScope,
  ): RuntimeValidationScope {
    if (!scope.id?.trim()) {
      throw new Error("Runtime validation scope id is required");
    }

    const normalizedScope: RuntimeValidationScope = {
      id: scope.id,
      active: scope.active !== false,
      startTaskNames: scope.startTaskNames
        ? [...new Set(scope.startTaskNames)]
        : undefined,
      startRoutineNames: scope.startRoutineNames
        ? [...new Set(scope.startRoutineNames)]
        : undefined,
      policy: scope.policy ? { ...scope.policy } : undefined,
    };

    this.runtimeValidationScopes.set(scope.id, normalizedScope);
    this.emittedMissingSchemaWarnings.clear();
    return {
      ...normalizedScope,
      startTaskNames: normalizedScope.startTaskNames
        ? [...normalizedScope.startTaskNames]
        : undefined,
      startRoutineNames: normalizedScope.startRoutineNames
        ? [...normalizedScope.startRoutineNames]
        : undefined,
      policy: normalizedScope.policy ? { ...normalizedScope.policy } : undefined,
    };
  }

  public static removeRuntimeValidationScope(id: string): void {
    this.runtimeValidationScopes.delete(id);
    this.emittedMissingSchemaWarnings.clear();
  }

  public static clearRuntimeValidationScopes(): void {
    this.runtimeValidationScopes.clear();
    this.emittedMissingSchemaWarnings.clear();
  }

  public static applyRuntimeValidationScopesToContext(
    context: AnyObject,
    routineName: string,
    tasks: Task[],
  ): AnyObject {
    const existingScopeIds = new Set<string>();
    const metadataScopeIds = context.__metadata?.__runtimeValidationScopeIds;
    const rootScopeIds = context.__runtimeValidationScopeIds;

    if (Array.isArray(rootScopeIds)) {
      rootScopeIds.forEach((id) => existingScopeIds.add(id));
    }

    if (Array.isArray(metadataScopeIds)) {
      metadataScopeIds.forEach((id: string) => existingScopeIds.add(id));
    }

    const taskNames = new Set(tasks.map((task) => task.name));
    for (const scope of this.runtimeValidationScopes.values()) {
      if (scope.active === false) {
        continue;
      }

      const matchesRoutine =
        scope.startRoutineNames?.includes(routineName) === true;
      const matchesTask =
        scope.startTaskNames?.some((taskName) => taskNames.has(taskName)) ===
        true;

      if (matchesRoutine || matchesTask) {
        existingScopeIds.add(scope.id);
      }
    }

    if (existingScopeIds.size > 0) {
      const scopeIds = Array.from(existingScopeIds);
      context.__runtimeValidationScopeIds = scopeIds;
      context.__metadata = {
        ...(context.__metadata ?? {}),
        __runtimeValidationScopeIds: scopeIds,
      };
    }

    return context;
  }

  public static resolveRuntimeValidationPolicyForTask(
    task: Task,
    metadata: AnyObject = {},
  ): ResolvedRuntimeValidationPolicy {
    const layer: "meta" | "business" =
      task.isMeta || task.isSubMeta || metadata.__isSubMeta
        ? "meta"
        : "business";
    const activeScopeIds = new Set<string>();

    const rootScopeIds = metadata.__runtimeValidationScopeIds;
    const nestedScopeIds = metadata.__metadata?.__runtimeValidationScopeIds;

    if (Array.isArray(rootScopeIds)) {
      rootScopeIds.forEach((id) => activeScopeIds.add(id));
    }

    if (Array.isArray(nestedScopeIds)) {
      nestedScopeIds.forEach((id: string) => activeScopeIds.add(id));
    }

    const mergedPolicy: RuntimeValidationPolicy = {
      ...this.runtimeValidationPolicy,
    };

    for (const scopeId of activeScopeIds) {
      const scope = this.runtimeValidationScopes.get(scopeId);
      if (!scope?.policy || scope.active === false) {
        continue;
      }

      Object.assign(mergedPolicy, scope.policy);
    }

    if (layer === "meta") {
      return {
        layer,
        inputMode: mergedPolicy.metaInput ?? "off",
        outputMode: mergedPolicy.metaOutput ?? "off",
        warnOnMissingInputSchema:
          mergedPolicy.warnOnMissingMetaInputSchema === true,
        warnOnMissingOutputSchema:
          mergedPolicy.warnOnMissingMetaOutputSchema === true,
        activeScopeIds: Array.from(activeScopeIds),
      };
    }

    return {
      layer,
      inputMode: mergedPolicy.businessInput ?? "off",
      outputMode: mergedPolicy.businessOutput ?? "off",
      warnOnMissingInputSchema:
        mergedPolicy.warnOnMissingBusinessInputSchema === true,
      warnOnMissingOutputSchema:
        mergedPolicy.warnOnMissingBusinessOutputSchema === true,
      activeScopeIds: Array.from(activeScopeIds),
    };
  }

  public static shouldEmitMissingSchemaWarning(cacheKey: string): boolean {
    if (this.emittedMissingSchemaWarnings.has(cacheKey)) {
      return false;
    }

    this.emittedMissingSchemaWarnings.add(cacheKey);
    return true;
  }

  /**
   * Creates an in-memory actor runtime instance.
   *
   * Actors are not graph nodes. Use `actor.task(...)` to produce standard tasks that
   * participate in the graph like any other task.
   */
  public static createActor<
    D extends Record<string, any> = AnyObject,
    R = AnyObject,
  >(spec: ActorSpec<D, R>, options: ActorFactoryOptions = {}): Actor<D, R> {
    this.assertGraphMutationAllowed("Cadenza.createActor");
    this.bootstrap();
    const actor = new Actor<D, R>(spec, options as ActorFactoryOptions<D, R>);
    this.registerActor(actor);
    return actor;
  }

  /**
   * Creates an actor from a serializable actor definition.
   *
   * Durable bootstrap state is resolved from `state.durable.initState`.
   * For backwards compatibility, legacy `state.durable.initialState` is also accepted.
   */
  public static createActorFromDefinition<
    D extends Record<string, any> = AnyObject,
    R = AnyObject,
  >(
    definition: ActorDefinition<D, R>,
    options: ActorFactoryOptions<D, R> = {},
  ): Actor<D, R> {
    this.bootstrap();

    if (!definition.description || !definition.description.trim()) {
      throw new Error(
        `Actor definition "${definition.name}" requires a non-empty description`,
      );
    }

    const spec: ActorSpec<D, R> = {
      name: definition.name,
      description: definition.description,
      defaultKey: definition.defaultKey,
      kind: definition.kind,
      loadPolicy: definition.loadPolicy,
      writeContract: definition.writeContract,
      consistencyProfile: definition.consistencyProfile,
      retry: definition.retry,
      idempotency: definition.idempotency,
      session: definition.session,
      runtimeReadGuard: definition.runtimeReadGuard,
      key: definition.key,
      state: definition.state,
      taskBindings: definition.tasks,
      initState:
        definition.state?.durable?.initState ??
        (
          definition.state?.durable as
            | { initialState?: D | (() => D) }
            | undefined
        )?.initialState,
    };

    const actorOptions: ActorFactoryOptions<D, R> = {
      isMeta: options.isMeta,
      definitionSource: definition,
      hydrateDurableState: options.hydrateDurableState,
    };

    return this.createActor(spec, actorOptions);
  }

  /**
   * Creates and registers a new task with the specified parameters and options.
   * Tasks are the basic building blocks of Cadenza graphs and are responsible for executing logic.
   * See {@link Task} for more information.
   *
   * @param {string} name - The unique name of the task.
   * @param {TaskFunction} func - The function to be executed by the task.
   * @param {string} [description] - An optional description for the task.
   * @param {TaskOptions} [options={}] - Configuration options for the task, such as concurrency, timeout, and retry settings.
   * @return {Task} The created task instance.
   *
   * @example
   * You can use arrow functions to create tasks.
   * ```ts
   * const task = Cadenza.createTask('My task', (ctx) => {
   *   console.log('My task executed with context:', ctx);
   * }, 'My task description');
   * ```
   *
   * You can also use named functions to create tasks.
   * This is the preferred way to create tasks since it allows for code inspection in the CadenzaUI.
   * ```ts
   * function myTask(ctx) {
   *   console.log('My task executed with context:', ctx);
   * }
   *
   * const task = Cadenza.createTask('My task', myTask);
   * ```
   *
   * ** Use the TaskOptions object to configure the task. **
   *
   * With concurrency limit, timeout limit and retry settings.
   * ```ts
   * Cadenza.createTask('My task', (ctx) => {
   *   console.log('My task executed with context:', ctx);
   * }, 'My task description', {
   *   concurrency: 10,
   *   timeout: 10000,
   *   retryCount: 3,
   *   retryDelay: 1000,
   *   retryDelayFactor: 1.5,
   * });
   * ```
   *
   * You can specify the input and output context schemas for the task.
   * ```ts
   * Cadenza.createTask('My task', (ctx) => {
   *   return { bar: 'foo' + ctx.foo };
   * }, 'My task description', {
   *   inputContextSchema: {
   *     type: 'object',
   *     properties: {
   *       foo: {
   *         type: 'string',
   *       },
   *     },
   *   required: ['foo'],
   *   },
   *   validateInputContext: true, // default is false
   *   outputContextSchema: {
   *     type: 'object',
   *     properties: {
   *       bar: {
   *         type: 'string',
   *       },
   *     },
   *     required: ['bar'],
   *   },
   *   validateOutputContext: true, // default is false
   * });
   * ```
   */
  static createTask(
    name: string,
    func: TaskFunction,
    description?: string,
    options: TaskOptions = {},
  ): Task {
    this.assertGraphMutationAllowed("Cadenza.createTask");
    this.bootstrap();
    this.validateName(name);

    const actorResolvedOptions = this.resolveTaskOptionsForActorTask(
      func,
      options,
    );

    options = {
      concurrency: 0,
      timeout: 0,
      register: true,
      isUnique: false,
      isMeta: false,
      isSubMeta: false,
      isHidden: false,
      getTagCallback: undefined,
      inputSchema: undefined,
      validateInputContext: false,
      outputSchema: undefined,
      validateOutputContext: false,
      retryCount: 0,
      retryDelay: 0,
      retryDelayMax: 0,
      retryDelayFactor: 1,
      ...actorResolvedOptions,
    };

    const createdTask = new Task(
      name,
      func,
      description,
      options.concurrency,
      options.timeout,
      options.register,
      options.isUnique,
      options.isMeta,
      options.isSubMeta,
      options.isHidden,
      options.getTagCallback,
      options.inputSchema,
      options.validateInputContext,
      options.outputSchema,
      options.validateOutputContext,
      options.retryCount,
      options.retryDelay,
      options.retryDelayMax,
      options.retryDelayFactor,
    );
    this.taskCache.set(name, createdTask);
    return createdTask;
  }

  public static createTaskFromDefinition(
    definition: RuntimeTaskDefinition,
  ): Task {
    this.bootstrap();
    this.validateName(definition.name);

    const existing = this.get(definition.name);
    if (existing) {
      if (!runtimeDefinitionRegistry.isRuntimeOwnedTask(definition.name)) {
        throw new Error(
          `Task "${definition.name}" already exists and is not runtime-owned`,
        );
      }
      existing.destroy();
    }

    const taskFunction = compileRuntimeTaskFunction(definition);
    const createdTask =
      definition.kind === "metaTask"
        ? this.createMetaTask(
            definition.name,
            taskFunction,
            definition.description ?? "",
            definition.options ?? {},
          )
        : this.createTask(
            definition.name,
            taskFunction,
            definition.description ?? "",
            definition.options ?? {},
          );

    runtimeDefinitionRegistry.setTaskDefinition(definition);
    return createdTask;
  }

  public static createHelper(
    name: string,
    func: HelperFunction,
    description: string = "",
  ): HelperDefinition {
    this.bootstrap();
    this.validateName(name);
    this.assertGraphMutationAllowed("Cadenza.createHelper");
    if (this.helperCache.has(name)) {
      throw new Error(`Helper "${name}" already exists`);
    }

    const helper = new HelperDefinition(name, func, description, false);
    this.helperCache.set(name, helper);
    this.emit("meta.helper.created", {
      data: {
        name,
        version: helper.version,
        description,
        functionString: func.toString(),
        isMeta: false,
      },
    });
    return helper;
  }

  public static createMetaHelper(
    name: string,
    func: HelperFunction,
    description: string = "",
  ): HelperDefinition {
    this.bootstrap();
    this.validateName(name);
    this.assertGraphMutationAllowed("Cadenza.createMetaHelper");
    if (this.helperCache.has(name)) {
      throw new Error(`Helper "${name}" already exists`);
    }

    const helper = new HelperDefinition(name, func, description, true);
    this.helperCache.set(name, helper);
    this.emit("meta.helper.created", {
      data: {
        name,
        version: helper.version,
        description,
        functionString: func.toString(),
        isMeta: true,
      },
    });
    return helper;
  }

  public static createHelperFromDefinition(
    definition: RuntimeHelperDefinition,
  ): HelperDefinition {
    this.bootstrap();
    this.validateName(definition.name);

    const existing = this.getHelper(definition.name);
    if (existing) {
      if (!runtimeDefinitionRegistry.isRuntimeOwnedHelper(definition.name)) {
        throw new Error(
          `Helper "${definition.name}" already exists and is not runtime-owned`,
        );
      }
      existing.destroy();
      this.helperCache.delete(definition.name);
    }

    const helperFunction = compileRuntimeHelperFunction(definition);
    const helper =
      definition.kind === "metaHelper"
        ? this.createMetaHelper(
            definition.name,
            helperFunction,
            definition.description ?? "",
          )
        : this.createHelper(
            definition.name,
            helperFunction,
            definition.description ?? "",
          );

    runtimeDefinitionRegistry.setHelperDefinition(definition);
    return helper;
  }

  public static createGlobal(
    name: string,
    value: unknown,
    description: string = "",
  ): GlobalDefinition {
    this.bootstrap();
    this.validateName(name);
    this.assertGraphMutationAllowed("Cadenza.createGlobal");
    if (this.globalCache.has(name)) {
      throw new Error(`Global "${name}" already exists`);
    }

    const globalDefinition = new GlobalDefinition(name, value, description, false);
    this.globalCache.set(name, globalDefinition);
    this.emit("meta.global.created", {
      data: {
        name,
        version: globalDefinition.version,
        description,
        isMeta: false,
        value: globalDefinition.value,
      },
    });
    return globalDefinition;
  }

  public static createMetaGlobal(
    name: string,
    value: unknown,
    description: string = "",
  ): GlobalDefinition {
    this.bootstrap();
    this.validateName(name);
    this.assertGraphMutationAllowed("Cadenza.createMetaGlobal");
    if (this.globalCache.has(name)) {
      throw new Error(`Global "${name}" already exists`);
    }

    const globalDefinition = new GlobalDefinition(name, value, description, true);
    this.globalCache.set(name, globalDefinition);
    this.emit("meta.global.created", {
      data: {
        name,
        version: globalDefinition.version,
        description,
        isMeta: true,
        value: globalDefinition.value,
      },
    });
    return globalDefinition;
  }

  public static createGlobalFromDefinition(
    definition: RuntimeGlobalDefinition,
  ): GlobalDefinition {
    this.bootstrap();
    this.validateName(definition.name);

    const existing = this.getGlobal(definition.name);
    if (existing) {
      if (!runtimeDefinitionRegistry.isRuntimeOwnedGlobal(definition.name)) {
        throw new Error(
          `Global "${definition.name}" already exists and is not runtime-owned`,
        );
      }
      existing.destroy();
      this.globalCache.delete(definition.name);
    }

    const globalDefinition =
      definition.kind === "metaGlobal"
        ? this.createMetaGlobal(
            definition.name,
            definition.value,
            definition.description ?? "",
          )
        : this.createGlobal(
            definition.name,
            definition.value,
            definition.description ?? "",
          );

    runtimeDefinitionRegistry.setGlobalDefinition(definition);
    return globalDefinition;
  }

  /**
   * Creates a meta task with the specified name, functionality, description, and options.
   * This is used for creating tasks that lives on the meta layer.
   * The meta layer is a special layer that is executed separately from the business logic layer and is used for extending Cadenzas core functionality.
   * See {@link Task} or {@link createTask} for more information.
   *
   * @param {string} name - The name of the meta task.
   * @param {TaskFunction} func - The function to be executed by the meta task.
   * @param {string} [description] - An optional description of the meta task.
   * @param {TaskOptions} [options={}] - Additional optional task configuration. Automatically sets `isMeta` to true.
   * @return {Task} A task instance configured as a meta task.
   */
  static createMetaTask(
    name: string,
    func: TaskFunction,
    description?: string,
    options: TaskOptions = {},
  ): Task {
    options.isMeta = true;
    return this.createTask(name, func, description, options);
  }

  /**
   * Creates a unique task by wrapping the provided task function with a uniqueness constraint.
   * Unique tasks are designed to execute once per execution ID, merging parents. This is useful for
   * tasks that require fan-in/joins after parallel branches.
   * See {@link Task} for more information.
   *
   * @param {string} name - The name of the task to be created.
   * @param {TaskFunction} func - The function that contains the logic for the task. It receives joinedContexts as a list in the context (context.joinedContexts).
   * @param {string} [description] - An optional description of the task.
   * @param {TaskOptions} [options={}] - Optional configuration for the task, such as additional metadata or task options.
   * @return {Task} The task instance that was created with a uniqueness constraint.
   *
   * @example
   * ```ts
   * const splitTask = Cadenza.createTask('Split foos', function* (ctx) {
   *   for (const foo of ctx.foos) {
   *     yield { foo };
   *   }
   * }, 'Splits a list of foos into multiple sub-branches');
   *
   * const processTask = Cadenza.createTask('Process foo', (ctx) => {
   *  return { bar: 'foo' + ctx.foo };
   * }, 'Process a foo');
   *
   * const uniqueTask = Cadenza.createUniqueTask('Gather processed foos', (ctx) => {
   *   // A unique task will always be provided with a list of contexts (ctx.joinedContexts) from its predecessors.
   *   const processedFoos = ctx.joinedContexts.map((c) => c.bar);
   *   return { foos: processedFoos };
   * }, 'Gathers together the processed foos.');
   *
   * splitTask.then(
   *   processTask.then(
   *     uniqueTask,
   *   ),
   * );
   *
   * // Give the flow a name using a routine
   * Cadenza.createRoutine(
   *   'Process foos',
   *   [splitTask],
   *   'Processes a list of foos'
   * ).doOn('main.received_foos'); // Subscribe to a signal
   *
   * // Trigger the flow from anywhere
   * Cadenza.emit('main.received_foos', { foos: ['foo1', 'foo2', 'foo3'] });
   * ```
   *
   */
  static createUniqueTask(
    name: string,
    func: TaskFunction,
    description?: string,
    options: TaskOptions = {},
  ): Task {
    this.assertGraphMutationAllowed("Cadenza.createUniqueTask");
    options.isUnique = true;
    return this.createTask(name, func, description, options);
  }

  /**
   * Creates a unique meta task with the specified name, function, description, and options.
   * See {@link createUniqueTask} and {@link createMetaTask} for more information.
   *
   * @param {string} name - The name of the task to create.
   * @param {TaskFunction} func - The function to execute when the task is run.
   * @param {string} [description] - An optional description of the task.
   * @param {TaskOptions} [options={}] - Optional settings for the task. Defaults to an empty object. Automatically sets `isMeta` and `isUnique` to true.
   * @return {Task} The created unique meta task.
   */
  static createUniqueMetaTask(
    name: string,
    func: TaskFunction,
    description?: string,
    options: TaskOptions = {},
  ): Task {
    options.isMeta = true;
    options.isUnique = true;
    return this.createUniqueTask(name, func, description, options);
  }

  /**
   * Creates a throttled task with a concurrency limit of 1, ensuring that only one instance of the task can run at a time for a specific throttle tag.
   * This is useful for ensuring execution order and preventing race conditions.
   * See {@link Task} for more information.
   *
   * @param {string} name - The name of the task.
   * @param {TaskFunction} func - The function to be executed when the task runs.
   * @param {ThrottleTagGetter} [throttledIdGetter=() => "default"] - A function that generates a throttle tag identifier to group tasks for throttling.
   * @param {string} [description] - An optional description of the task.
   * @param {TaskOptions} [options={}] - Additional options to customize the task behavior.
   * @return {Task} The created throttled task.
   *
   * @example
   * ```ts
   * const task = Cadenza.createThrottledTask(
   *   'My task',
   *   async (ctx) => {
   *      await new Promise((resolve) => setTimeout(resolve, 1000));
   *      console.log('My task executed with context:', ctx);
   *   },
   *   // Will throttle by the value of ctx.foo to make sure tasks with the same value are executed sequentially
   *   (ctx) => ctx.foo,
   * );
   *
   * Cadenza.run(task, { foo: 'bar' }); // (First execution)
   * Cadenza.run(task, { foo: 'bar' }); // This will be executed after the first execution is finished
   * Cadenza.run(task, { foo: 'baz' }); // This will be executed in parallel with the first execution
   * ```
   */
  static createThrottledTask(
    name: string,
    func: TaskFunction,
    throttledIdGetter: ThrottleTagGetter = () => "default",
    description?: string,
    options: TaskOptions = {},
  ): Task {
    this.assertGraphMutationAllowed("Cadenza.createThrottledTask");
    options.concurrency = 1;
    options.getTagCallback = throttledIdGetter;
    return this.createTask(name, func, description, options);
  }

  /**
   * Creates a throttled meta task with the specified configuration.
   * See {@link createThrottledTask} and {@link createMetaTask} for more information.
   *
   * @param {string} name - The name of the throttled meta task.
   * @param {TaskFunction} func - The task function to be executed.
   * @param {ThrottleTagGetter} throttledIdGetter - A function to retrieve the throttling identifier.
   * @param {string} [description] - An optional description of the task.
   * @param {TaskOptions} [options={}] - Additional options for configuring the task.
   * @return {Task} The created throttled meta task.
   */
  static createThrottledMetaTask(
    name: string,
    func: TaskFunction,
    throttledIdGetter: ThrottleTagGetter,
    description?: string,
    options: TaskOptions = {},
  ): Task {
    options.isMeta = true;
    return this.createThrottledTask(
      name,
      func,
      throttledIdGetter,
      description,
      options,
    );
  }

  /**
   * Creates and returns a new debounced task with the specified parameters.
   * This is useful to prevent rapid execution of tasks that may be triggered by multiple events within a certain time frame.
   * See {@link DebounceTask} for more information.
   *
   * @param {string} name - The unique name of the task to be created.
   * @param {TaskFunction} func - The function to be executed by the task.
   * @param {string} [description] - An optional description of the task.
   * @param {number} [debounceTime=1000] - The debounce time in milliseconds to delay the execution of the task.
   * @param {TaskOptions & DebounceOptions} [options={}] - Additional configuration options for the task, including debounce behavior and other task properties.
   * @return {DebounceTask} A new instance of the DebounceTask with the specified configuration.
   *
   * @example
   * ```ts
   * const task = Cadenza.createDebounceTask(
   *   'My debounced task',
   *   (ctx) => {
   *      console.log('My task executed with context:', ctx);
   *   },
   *   'My debounced task description',
   *   100, // Debounce time in milliseconds. Default is 1000
   *   {
   *     leading: false, // Should the first execution of a burst be executed immediately? Default is false
   *     trailing: true, // Should the last execution of a burst be executed? Default is true
   *     maxWait: 1000, // Maximum time in milliseconds to wait for the next execution. Default is 0
   *   },
   * );
   *
   * Cadenza.run(task, { foo: 'bar' }); // This will not be executed
   * Cadenza.run(task, { foo: 'bar' }); // This will not be executed
   * Cadenza.run(task, { foo: 'baz' }); // This execution will be delayed by 100ms
   * ```
   */
  static createDebounceTask(
    name: string,
    func: TaskFunction,
    description?: string,
    debounceTime: number = 1000,
    options: TaskOptions & DebounceOptions = {},
  ): DebounceTask {
    this.assertGraphMutationAllowed("Cadenza.createDebounceTask");
    this.bootstrap();
    this.validateName(name);

    const actorResolvedOptions = this.resolveTaskOptionsForActorTask(
      func,
      options,
    );

    options = {
      concurrency: 0,
      timeout: 0,
      register: true,
      leading: false,
      trailing: true,
      maxWait: 0,
      isUnique: false,
      isMeta: false,
      isSubMeta: false,
      isHidden: false,
      inputSchema: undefined,
      validateInputContext: false,
      outputSchema: undefined,
      validateOutputContext: false,
      ...actorResolvedOptions,
    };

    const createdTask = new DebounceTask(
      name,
      func,
      description,
      debounceTime,
      options.leading,
      options.trailing,
      options.maxWait,
      options.concurrency,
      options.timeout,
      options.register,
      options.isUnique,
      options.isMeta,
      options.isSubMeta,
      options.isHidden,
      options.inputSchema,
      options.validateInputContext,
      options.outputSchema,
      options.validateOutputContext,
    );
    this.taskCache.set(name, createdTask);
    return createdTask;
  }

  /**
   * Creates a debounced meta task with the specified parameters.
   * See {@link createDebounceTask} and {@link createMetaTask} for more information.
   *
   * @param {string} name - The name of the task.
   * @param {TaskFunction} func - The function to be executed by the task.
   * @param {string} [description] - Optional description of the task.
   * @param {number} [debounceTime=1000] - The debounce delay in milliseconds.
   * @param {TaskOptions & DebounceOptions} [options={}] - Additional configuration options for the task.
   * @return {DebounceTask} Returns an instance of the debounced meta task.
   */
  static createDebounceMetaTask(
    name: string,
    func: TaskFunction,
    description?: string,
    debounceTime: number = 1000,
    options: TaskOptions & DebounceOptions = {},
  ): DebounceTask {
    options.isMeta = true;
    return this.createDebounceTask(
      name,
      func,
      description,
      debounceTime,
      options,
    );
  }

  /**
   * Creates an ephemeral task with the specified configuration.
   * Ephemeral tasks are designed to self-destruct after execution or a certain condition is met.
   * This is useful for transient tasks such as resolving promises or performing cleanup operations.
   * They are not registered by default.
   * See {@link EphemeralTask} for more information.
   *
   * @param {string} name - The name of the task to be created.
   * @param {TaskFunction} func - The function that defines the logic of the task.
   * @param {string} [description] - An optional description of the task.
   * @param {TaskOptions & EphemeralTaskOptions} [options={}] - The configuration options for the task, including concurrency, timeouts, and retry policies.
   * @return {EphemeralTask} The created ephemeral task instance.
   *
   * @example
   * By default, ephemeral tasks are executed once and destroyed after execution.
   * ```ts
   * const task = Cadenza.createEphemeralTask('My ephemeral task', (ctx) => {
   *   console.log('My task executed with context:', ctx);
   * });
   *
   * Cadenza.run(task); // Executes the task once and destroys it after execution
   * Cadenza.run(task); // Does nothing, since the task is destroyed
   * ```
   *
   * Use destroy condition to conditionally destroy the task
   * ```ts
   * const task = Cadenza.createEphemeralTask(
   *   'My ephemeral task',
   *   (ctx) => {
   *      console.log('My task executed with context:', ctx);
   *   },
   *   'My ephemeral task description',
   *   {
   *     once: false, // Should the task be executed only once? Default is true
   *     destroyCondition: (ctx) => ctx.foo > 10, // Should the task be destroyed after execution? Default is undefined
   *   },
   * );
   *
   * Cadenza.run(task, { foo: 5 }); // The task will not be destroyed and can still be executed
   * Cadenza.run(task, { foo: 10 }); // The task will not be destroyed and can still be executed
   * Cadenza.run(task, { foo: 20 }); // The task will be destroyed after execution and cannot be executed anymore
   * Cadenza.run(task, { foo: 30 }); // This will not be executed
   * ```
   *
   * A practical use case for ephemeral tasks is to resolve a promise upon some external event.
   * ```ts
   * const task = Cadenza.createTask('Confirm something', (ctx, emit) => {
   *   return new Promise((resolve) => {
   *     ctx.foo = uuid();
   *
   *     Cadenza.createEphemeralTask(`Resolve promise of ${ctx.foo}`, (c) => {
   *       console.log('My task executed with context:', ctx);
   *       resolve(c);
   *     }).doOn(`socket.confirmation_received:${ctx.foo}`);
   *
   *     emit('this_domain.confirmation_requested', ctx);
   *   });
   * });
   * ```
   */
  static createEphemeralTask(
    name: string,
    func: TaskFunction,
    description?: string,
    options: TaskOptions & EphemeralTaskOptions = {},
  ): EphemeralTask {
    this.assertGraphMutationAllowed("Cadenza.createEphemeralTask");
    this.bootstrap();
    this.validateName(name);

    const actorResolvedOptions = this.resolveTaskOptionsForActorTask(
      func,
      options,
    );

    options = {
      concurrency: 0,
      timeout: 0,
      register: true,
      isUnique: false,
      isMeta: false,
      isSubMeta: false,
      isHidden: false,
      once: true,
      destroyCondition: () => true,
      getTagCallback: undefined,
      inputSchema: undefined,
      validateInputContext: false,
      outputSchema: undefined,
      validateOutputContext: false,
      retryCount: 0,
      retryDelay: 0,
      retryDelayMax: 0,
      retryDelayFactor: 1,
      ...actorResolvedOptions,
    };

    const createdTask = new EphemeralTask(
      name,
      func,
      description,
      options.once,
      options.destroyCondition,
      options.concurrency,
      options.timeout,
      options.register,
      options.isUnique,
      options.isMeta,
      options.isSubMeta,
      options.isHidden,
      options.getTagCallback,
      options.inputSchema,
      options.validateInputContext,
      options.outputSchema,
      options.validateOutputContext,
      options.retryCount,
      options.retryDelay,
      options.retryDelayMax,
      options.retryDelayFactor,
    );
    this.taskCache.set(name, createdTask);
    return createdTask;
  }

  /**
   * Creates an ephemeral meta task with the specified name, function, description, and options.
   * See {@link createEphemeralTask} and {@link createMetaTask} for more details.
   *
   * @param {string} name - The name of the task to be created.
   * @param {TaskFunction} func - The function to be executed as part of the task.
   * @param {string} [description] - An optional description of the task.
   * @param {TaskOptions & EphemeralTaskOptions} [options={}] - Additional options for configuring the task.
   * @return {EphemeralTask} The created ephemeral meta task.
   */
  static createEphemeralMetaTask(
    name: string,
    func: TaskFunction,
    description?: string,
    options: TaskOptions & EphemeralTaskOptions = {},
  ): EphemeralTask {
    options.isMeta = true;
    return this.createEphemeralTask(name, func, description, options);
  }

  /**
   * Creates a new routine with the specified name, tasks, and an optional description.
   * Routines are named entry points to starting tasks and are registered in the GraphRegistry.
   * They are used to group tasks together and provide a high-level structure for organizing and managing the execution of a set of tasks.
   * See {@link GraphRoutine} for more information.
   *
   * @param {string} name - The name of the routine to create.
   * @param {Task[]} tasks - A list of tasks to include in the routine.
   * @param {string} [description=""] - An optional description for the routine.
   * @return {GraphRoutine} A new instance of the GraphRoutine containing the specified tasks and description.
   *
   * @example
   * ```ts
   * const task1 = Cadenza.createTask("Task 1", () => {});
   * const task2 = Cadenza.createTask("Task 2", () => {});
   *
   * task1.then(task2);
   *
   * const routine = Cadenza.createRoutine("Some routine", [task1]);
   *
   * Cadenza.run(routine);
   *
   * // Or, routines can be triggered by signals
   * routine.doOn("some.signal");
   *
   * Cadenza.emit("some.signal", {});
   * ```
   */
  static createRoutine(
    name: string,
    tasks: Task[],
    description: string = "",
  ): GraphRoutine {
    this.assertGraphMutationAllowed("Cadenza.createRoutine");
    this.bootstrap();
    this.validateName(name);
    if (tasks.length === 0) {
      throw new Error(`Routine '${name}' created with no starting tasks.`);
    }
    const createdRoutine = new GraphRoutine(name, tasks, description);
    this.routineCache.set(name, createdRoutine);
    return createdRoutine;
  }

  public static createRoutineFromDefinition(
    definition: RuntimeRoutineDefinition,
  ): GraphRoutine {
    const existing = this.getRoutine(definition.name);
    if (existing) {
      if (!runtimeDefinitionRegistry.isRuntimeOwnedRoutine(definition.name)) {
        throw new Error(
          `Routine "${definition.name}" already exists and is not runtime-owned`,
        );
      }
      existing.destroy();
    }

    const startTasks = definition.startTaskNames.map((taskName) => {
      const task = this.get(taskName);
      if (!task) {
        throw new Error(
          `Routine "${definition.name}" references missing task "${taskName}"`,
        );
      }
      return task;
    });

    const routine =
      definition.isMeta === true
        ? this.createMetaRoutine(
            definition.name,
            startTasks,
            definition.description ?? "",
          )
        : this.createRoutine(
            definition.name,
            startTasks,
            definition.description ?? "",
          );

    runtimeDefinitionRegistry.setRoutineDefinition(definition);
    return routine;
  }

  /**
   * Creates a meta routine with a given name, tasks, and optional description.
   * Routines are named entry points to starting tasks and are registered in the GraphRegistry.
   * They are used to group tasks together and provide a high-level structure for organizing and managing the execution of a set of tasks.
   * See {@link GraphRoutine} and {@link createRoutine} for more information.
   *
   * @param {string} name - The name of the routine to be created.
   * @param {Task[]} tasks - An array of tasks that the routine will consist of.
   * @param {string} [description=""] - An optional description for the routine.
   * @return {GraphRoutine} A new instance of the `GraphRoutine` representing the created routine.
   * @throws {Error} If no starting tasks are provided.
   */
  static createMetaRoutine(
    name: string,
    tasks: Task[],
    description: string = "",
  ): GraphRoutine {
    this.assertGraphMutationAllowed("Cadenza.createMetaRoutine");
    this.bootstrap();
    this.validateName(name);
    if (tasks.length === 0) {
      throw new Error(`Routine '${name}' created with no starting tasks.`);
    }
    const createdRoutine = new GraphRoutine(name, tasks, description, true);
    this.routineCache.set(name, createdRoutine);
    return createdRoutine;
  }

  public static snapshotRuntime(): RuntimeSnapshot {
    const taskMap = new Map<string, Task>();
    for (const task of this.taskCache.values()) {
      taskMap.set(task.name, task);
    }
    for (const task of this.registry?.tasks.values() ?? []) {
      taskMap.set(task.name, task);
    }

    const tasks = Array.from(taskMap.values()).map((task) => {
      const runtimeTaskDefinition = runtimeDefinitionRegistry.taskDefinitions.get(
        task.name,
      );
      const runtimeActorTaskDefinition =
        runtimeDefinitionRegistry.actorTaskDefinitions.get(task.name);

      return {
        name: task.name,
        version: task.version,
        description: task.description,
        kind: (runtimeActorTaskDefinition
          ? "actorTask"
          : task.isMeta
            ? "metaTask"
            : "task") as "task" | "metaTask" | "actorTask",
        runtimeOwned:
          runtimeDefinitionRegistry.isRuntimeOwnedTask(task.name) === true,
        language:
          runtimeTaskDefinition?.language ??
          runtimeActorTaskDefinition?.language ??
          null,
        handlerSource:
          runtimeTaskDefinition?.handlerSource ??
          runtimeActorTaskDefinition?.handlerSource ??
          null,
        concurrency: task.concurrency,
        timeout: task.timeout,
        retryCount: task.retryCount,
        retryDelay: task.retryDelay,
        retryDelayMax: task.retryDelayMax,
        retryDelayFactor: task.retryDelayFactor,
        validateInputContext: task.validateInputContext,
        validateOutputContext: task.validateOutputContext,
        inputContextSchema: sanitizeForJson(task.inputContextSchema) as Record<
          string,
          unknown
        >,
        outputContextSchema: sanitizeForJson(task.outputContextSchema) as Record<
          string,
          unknown
        >,
        nextTaskNames: Array.from(task.nextTasks).map((nextTask) => nextTask.name),
        predecessorTaskNames: Array.from(task.predecessorTasks).map(
          (predecessorTask) => predecessorTask.name,
        ),
        signals: {
          emits: Array.from(task.emitsSignals),
          emitsAfter: Array.from(task.signalsToEmitAfter),
          emitsOnFail: Array.from(task.signalsToEmitOnFail),
          observed: Array.from(task.observedSignals),
        },
        intents: {
          handles: Array.from(task.handlesIntents),
          inquires: Array.from(task.inquiresIntents),
        },
        tools: {
          helpers: Object.fromEntries(task.helperAliases),
          globals: Object.fromEntries(task.globalAliases),
        },
        actorName: runtimeActorTaskDefinition?.actorName ?? null,
        actorMode: runtimeActorTaskDefinition?.mode ?? null,
      };
    });

    const helpers = this.getAllHelpers().map((helper) => {
      const runtimeHelperDefinition =
        runtimeDefinitionRegistry.helperDefinitions.get(helper.name);

      return {
        name: helper.name,
        version: helper.version,
        description: helper.description,
        kind: (helper.isMeta ? "metaHelper" : "helper") as
          | "helper"
          | "metaHelper",
        runtimeOwned: runtimeDefinitionRegistry.isRuntimeOwnedHelper(helper.name),
        language: runtimeHelperDefinition?.language ?? null,
        handlerSource: runtimeHelperDefinition?.handlerSource ?? null,
        tools: {
          helpers: Object.fromEntries(helper.helperAliases),
          globals: Object.fromEntries(helper.globalAliases),
        },
      };
    });

    const globals = this.getAllGlobals().map((globalDefinition) => ({
      name: globalDefinition.name,
      version: globalDefinition.version,
      description: globalDefinition.description,
      kind: (globalDefinition.isMeta ? "metaGlobal" : "global") as
        | "global"
        | "metaGlobal",
      runtimeOwned: runtimeDefinitionRegistry.isRuntimeOwnedGlobal(
        globalDefinition.name,
      ),
      value: sanitizeForJson(globalDefinition.value),
    }));

    const routineMap = new Map<string, GraphRoutine>();
    for (const routine of this.routineCache.values()) {
      routineMap.set(routine.name, routine);
    }
    for (const routine of this.registry?.routines.values() ?? []) {
      routineMap.set(routine.name, routine);
    }

    const routines = Array.from(routineMap.values()).map(
      (routine) => ({
        name: routine.name,
        version: routine.version,
        description: routine.description,
        isMeta: routine.isMeta,
        runtimeOwned:
          runtimeDefinitionRegistry.isRuntimeOwnedRoutine(routine.name) === true,
        startTaskNames: Array.from(routine.tasks).map((task) => task.name),
        observedSignals: Array.from(routine.observedSignals),
      }),
    );

    const intents = Array.from(this.inquiryBroker?.intents.values() ?? []).map(
      (intent) => {
        const sanitizedIntent = sanitizeForJson(intent) as Record<
          string,
          unknown
        >;
        return {
          ...sanitizedIntent,
          runtimeOwned: runtimeDefinitionRegistry.intentDefinitions.has(
            intent.name,
          ),
        };
      },
    );

    const signals = Array.from(
      this.signalBroker?.emittedSignalsRegistry.values() ?? [],
    ).map((signalName) => ({
      name: signalName,
      metadata:
        (sanitizeForJson(
          this.signalBroker?.signalMetadataRegistry.get(signalName) ?? null,
        ) as Record<string, unknown> | null) ?? null,
    }));

    const actors = this.getAllActors().map((actor) => {
      const definition = actor.toDefinition() as ActorDefinition<
        Record<string, any>,
        Record<string, any>
      >;
      const actorKeys = actor.listActorKeys().map((actorKey) => ({
        actorKey,
        durableState: sanitizeForJson(actor.getDurableState(actorKey)),
        runtimeState: sanitizeForJson(actor.getRuntimeState(actorKey)),
        durableVersion: actor.getDurableVersion(actorKey),
        runtimeVersion: actor.getRuntimeVersion(actorKey),
      }));

      return {
        name: actor.spec.name,
        description: actor.spec.description ?? "",
        runtimeOwned: runtimeDefinitionRegistry.isRuntimeOwnedActor(
          actor.spec.name,
        ),
        definition: sanitizeForJson(definition) as ActorDefinition<
          Record<string, any>,
          Record<string, any>
        >,
        actorKeys,
      };
    });

    const actorTasks = Array.from(
      runtimeDefinitionRegistry.actorTaskDefinitions.values(),
    ).map((definition: RuntimeActorTaskDefinition) => ({
      actorName: definition.actorName,
      taskName: definition.taskName,
      description: definition.description ?? "",
      mode: definition.mode ?? "read",
      language: definition.language,
      handlerSource: definition.handlerSource,
      runtimeOwned: true,
    }));

    const links = Array.from(runtimeDefinitionRegistry.taskLinks.values()).map(
      (link) => ({ ...link }),
    );

    return {
      runtimeMode: "core",
      bootstrapped: this.isBootstrapped,
      mode: this.mode,
      tasks,
      helpers,
      globals,
      routines,
      intents: intents as RuntimeSnapshot["intents"],
      signals,
      actors: actors as RuntimeSnapshot["actors"],
      actorTasks,
      links,
    };
  }

  static reset() {
    this.signalBroker?.reset();
    this.inquiryBroker?.reset();
    this.registry?.reset();
    this.taskCache.clear();
    this.routineCache.clear();
    this.actorCache.clear();
    this.helperCache.clear();
    this.globalCache.clear();
    this.runtimeInquiryDelegate = undefined;
    this.runtimeValidationPolicy = {};
    this.runtimeValidationScopes.clear();
    this.emittedMissingSchemaWarnings.clear();
    this.helperExecutionDepth = 0;
    runtimeDefinitionRegistry.reset();
    this.isBootstrapped = false;
  }
}
